import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { previewDoc, readProbeMessage, type MapProbeResult } from "../../../../lib/googleMaps";

/**
 * One real map, drawn with one key, inside its own iframe.
 *
 * The frame is how the panel tests a key without ever loading the Maps script
 * into the CRM itself: the script may only be loaded once per page with one
 * key, so a panel that used the page's own loader could be used exactly once
 * and would then keep answering about the first key it ever saw. A fresh
 * `srcdoc` per attempt has no such memory.
 *
 * It is also VISIBLE on purpose - see the warning at the top of
 * `lib/googleMaps.ts`. A hidden map never asks Google to authenticate, so a
 * hidden probe cannot tell a good key from a bad one.
 */
export function MapKeyPreview({
  mapKey,
  attempt,
  onResult,
}: {
  mapKey: string;
  /** Bumped by the parent to re-run the same key. Part of the frame's identity,
   *  so pressing the button twice really does mount a second frame. */
  attempt: number;
  onResult: (result: MapProbeResult | null) => void;
}) {
  const t = useT();
  const [pending, setPending] = useState(true);
  // The callback is read from a ref inside the effect: a parent that passes an
  // inline arrow would otherwise remount the frame - and re-bill a map load -
  // on every one of its own renders.
  const cb = useRef(onResult);
  cb.current = onResult;

  const token = `wa-maps-${attempt}`;
  const [doc, setDoc] = useState("");
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trimmed = mapKey.trim();
    if (!trimmed) return;

    setPending(true);
    cb.current(null);

    // No timeout that becomes a verdict. Silence means the frame had nothing
    // certain to report, and the map on screen is the answer in that case.
    function onMessage(event: MessageEvent) {
      const result = readProbeMessage(event, token);
      if (!result) return;
      window.removeEventListener("message", onMessage);
      cb.current(result);
    }

    window.addEventListener("message", onMessage);
    setDoc(previewDoc(trimmed, token));

    // Not a nicety. Maps defers tile loading - and with it the authentication
    // round trip - until the map has a visible area, so a preview sitting below
    // the fold never proves anything and comes back `hidden`.
    box.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });

    return () => window.removeEventListener("message", onMessage);
  }, [mapKey, token]);

  if (!doc) return null;

  return (
    <div
      ref={box}
      className="relative overflow-hidden rounded-[20px] border border-[var(--crm-hair)]"
      style={{ height: 220 }}
    >
      <iframe
        key={token}
        srcDoc={doc}
        title={t("settings.maps.previewTitle")}
        className="block h-full w-full border-0"
        // The overlay clears when the document is up, not when a verdict lands:
        // a verdict may never land, and a spinner that waits for one would spin
        // forever over a perfectly good map.
        onLoad={() => setPending(false)}
        // Deliberately NOT sandboxed. `sandbox` without `allow-same-origin`
        // gives the frame an opaque origin, and an opaque origin sends no
        // usable Referer - which would make every referrer-restricted key look
        // broken here and working in the real map view. The frame runs Google's
        // own script, the same script the map view loads directly.
        referrerPolicy="strict-origin-when-cross-origin"
      />
      {pending ? (
        <div
          className="absolute inset-0 flex items-center justify-center gap-2 text-[12.5px] text-muted-foreground"
          style={{ background: "var(--crm-page)" }}
        >
          <Loader2 className="size-4 animate-spin" />
          {t("settings.maps.previewLoading")}
        </div>
      ) : null}
    </div>
  );
}
